import { ElectricityProductionMethodConfig, MethodEmissionReport } from '@/api/EmissionReportModel'
import { SelectedElectricityProductionMethod } from '@/features/production-routes/api/ElectricityProductionMethodsModel'
import {
  Button,
  Card,
  CardBody,
  InfoTooltip,
  Table,
  TableCell,
  TableColumnHeader,
  TableRow,
  TableRowHeader,
  Alert,
  ElectricityProductionMethod
} from '@/common'
import { ReportSectionCardHeader } from '../SectionCardHeader'
import { useTranslation } from 'react-i18next'
import { useCallback, useMemo, useState } from 'react'
import { useUpdateEmissionReport } from '@/hooks/api/emissionReports'
import { UnitLabel } from '@/shared/config/config'
import useDialog from '@/common/hooks/useDialog'
import ProductionMethodConsumptionEditDialog from './ProductionMethodConsumptionInputDialog'
import { MethodCalculatedEmission, ReportCalculatedEmissions } from '@/shared/api/types'
import { LightningIcon, PlusIcon } from '@/common/components/icons'
import ElectricitySourceDialog from './ElectricitySourceDialog'
import LocalizedNumberFlow from '@/components/LocalizednNumberFlow'
import NumberFlow from '@number-flow/react'
import { convertTjToMwh } from '@/utils'

function SourceConfigurationTable({
  electricityProductionMethods,
  config,
  isViewMode,
  startEditMethodDialog
}: {
  electricityProductionMethods: SelectedElectricityProductionMethod[]
  config: Record<string, ElectricityProductionMethodConfig>
  isViewMode: boolean
  startEditMethodDialog: (method: ElectricityProductionMethod) => void
}) {
  const { t } = useTranslation()

  return (
    <Table dense className="table-fixed">
      <thead>
        <TableRow>
          <TableColumnHeader className="w-52">
            {t('report:electricity.sourceConfiguration.table.source')}
          </TableColumnHeader>
          <TableColumnHeader className="w-48 text-right">
            {t('report:electricity.sourceConfiguration.table.amount')}
            <br /> ({UnitLabel.MWh})
          </TableColumnHeader>
          <TableColumnHeader className="w-48 text-right">
            {t('report:electricity.sourceConfiguration.table.emissionFactor')} <br />(
            {UnitLabel.tCO2PerMWh})
          </TableColumnHeader>
          <TableColumnHeader className="w-32"></TableColumnHeader>
        </TableRow>
      </thead>
      <tbody>
        {electricityProductionMethods.map(method => {
          const isRenewable = method.isRenewable
          const configForMethod = config[method.type]
          const hasValue = configForMethod.consumption !== undefined
          return (
            <TableRow key={method.type}>
              <TableRowHeader className="text-left">
                {t(`common:electricitySources.${method.type}.title`)}
                {isRenewable && (
                  <span className="text-sm font-normal text-text-secondary ml-1">
                    ({t('report:electricity.renewable')})
                  </span>
                )}
              </TableRowHeader>
              <TableCell className="text-right">
                {hasValue ? (
                  <LocalizedNumberFlow value={convertTjToMwh(configForMethod.consumption)} />
                ) : (
                  '-'
                )}
              </TableCell>
              <TableCell className="text-right">
                {hasValue ? <LocalizedNumberFlow value={configForMethod.emissionFactor} /> : '-'}
              </TableCell>
              <TableCell className="text-right">
                <Button
                  disabled={isViewMode}
                  onClick={() => startEditMethodDialog(method.type)}
                  sizeType="sm"
                  variant="txt-default"
                >
                  {t('common:actions.edit')}
                </Button>
              </TableCell>
            </TableRow>
          )
        })}
      </tbody>
    </Table>
  )
}

export default function IntegratedElectricityReportSection({
  electricityProductionMethods,
  gridEmissionFactor,
  reportId,
  isViewMode,
  configByElectricityMethod: config,
  methodEmissionReports,
  calculatedEmissionsForAllMethods
}: {
  electricityProductionMethods: SelectedElectricityProductionMethod[]
  isComplete: boolean
  gridEmissionFactor: number
  reportId: string
  isViewMode: boolean
  configByElectricityMethod: Record<string, ElectricityProductionMethodConfig>
  methodEmissionReports: MethodEmissionReport[]
  calculatedEmissionsForAllMethods: ReportCalculatedEmissions
}) {
  const { t } = useTranslation()

  const { mutateAsync: updateEmissionReport, isPending: isUpdatingEmissionReport } =
    useUpdateEmissionReport(reportId)

  const [isEditMethodDialogOpen, setIsEditMethodDialogOpen] = useState(false)
  const {
    openDialog: openEditProductionMethodConsumptionDialog,
    closeDialog: closeEditProductionMethodConsumptionDialog,
    open: isEditProductionMethodConsumptionDialogOpen
  } = useDialog()
  const [currentEditedElectricityMethodType, setCurrentEditedElectricityMethodType] =
    useState<ElectricityProductionMethod | null>(null)

  const [currentMethodReportId, setCurrentMethodReportId] = useState<string | null>(null)

  const currentReportedMethod = currentEditedElectricityMethodType
    ? electricityProductionMethods.find(
        method => method.type === currentEditedElectricityMethodType
      )
    : null

  const isCurrentEditedMethodRenewable = useMemo(() => {
    return currentReportedMethod?.isRenewable || false
  }, [currentReportedMethod])

  const startEditMethodDialog = (method: ElectricityProductionMethod) => {
    setCurrentEditedElectricityMethodType(method)
    setIsEditMethodDialogOpen(true)
  }

  const closeEditMethodDialog = useCallback(() => {
    setIsEditMethodDialogOpen(false)
  }, [])

  const resetCurrentEditedMethod = useCallback(() => {
    setCurrentEditedElectricityMethodType(null)
  }, [])

  const initProductionMethodEdit = useCallback(
    (methodReportId: string) => {
      setCurrentMethodReportId(methodReportId)
      openEditProductionMethodConsumptionDialog()
    },
    [openEditProductionMethodConsumptionDialog]
  )

  const closeProductionMethodEdit = useCallback(() => {
    closeEditProductionMethodConsumptionDialog()
  }, [closeEditProductionMethodConsumptionDialog])

  const resetProductionMethodEdit = useCallback(() => {
    setCurrentMethodReportId(null)
  }, [])

  const saveElectricityConfig = useCallback(
    async (method: ElectricityProductionMethod, payload: ElectricityProductionMethodConfig) => {
      const mergedConfig = {
        ...config,
        [method]: payload
      }
      await updateEmissionReport({
        electricityProductionConfig: mergedConfig
      })
    },
    [config, updateEmissionReport]
  )

  const calculatedEmissionsForAllMethodsById = useMemo(() => {
    return calculatedEmissionsForAllMethods.reduce(
      (acc, method) => {
        acc[method.id] = method
        return acc
      },
      {} as Record<string, MethodCalculatedEmission>
    )
  }, [calculatedEmissionsForAllMethods])

  const editedMethodEmissionReport = useMemo(() => {
    return methodEmissionReports.find(
      methodEmissionReport => methodEmissionReport.id === currentMethodReportId
    )
  }, [methodEmissionReports, currentMethodReportId])

  const editedProductionMethodName = useMemo(() => {
    const editedProductionMethodCode = editedMethodEmissionReport?.productionMethodCode
    return t(`common:productionMethods.${editedProductionMethodCode}`)
  }, [editedMethodEmissionReport, t])

  const hasAtLeastOneMethodConfig = useMemo(() => {
    return Object.values(config).some(config => {
      return config.consumption !== null
    })
  }, [config])

  const hasAtLeastOneMethodEmissionReport = useMemo(() => {
    return methodEmissionReports.some(
      methodEmissionReport => methodEmissionReport.electricity?.consumptionTj
    )
  }, [methodEmissionReports])

  const numberOfReportedMethods = useMemo(() => {
    return methodEmissionReports.filter(
      methodEmissionReport =>
        methodEmissionReport.electricity?.consumptionTj! >= 0 &&
        methodEmissionReport.electricity?.consumptionTj !== undefined
    ).length
  }, [methodEmissionReports])

  const numberOfReportedSources = useMemo(() => {
    return electricityProductionMethods.filter(method => {
      const configForMethod = config[method.type]
      return (
        configForMethod?.consumption >= 0 &&
        configForMethod?.consumption !== undefined &&
        configForMethod?.consumption !== null
      )
    }).length
  }, [electricityProductionMethods, config])

  const numberOfProductionMethods = methodEmissionReports.length
  const numberOfElectricitySources = electricityProductionMethods.length

  const qualifyingSourceTypes: ElectricityProductionMethod[] = useMemo(
    () => [
      ElectricityProductionMethod.GRID,
      ElectricityProductionMethod.DIRECT_TECHNICAL_LINK,
      ElectricityProductionMethod.PPA
    ],
    []
  )

  const qualifyingSourcesInRoute = useMemo(
    () =>
      electricityProductionMethods.filter(method => qualifyingSourceTypes.includes(method.type)),
    [electricityProductionMethods, qualifyingSourceTypes]
  )

  const areAllQualifyingSourcesZero = useMemo(() => {
    return qualifyingSourcesInRoute.every(method => (config[method.type]?.consumption ?? 0) === 0)
  }, [qualifyingSourcesInRoute, config])

  const completionPercentage = useMemo(() => {
    if (areAllQualifyingSourcesZero) {return 0}

    return (
      ((numberOfReportedMethods + numberOfReportedSources) /
        (numberOfElectricitySources + numberOfProductionMethods)) *
      100
    )
  }, [
    numberOfReportedMethods,
    numberOfReportedSources,
    numberOfElectricitySources,
    numberOfProductionMethods,
    areAllQualifyingSourcesZero
  ])

  const showZeroConsumptionWarning = areAllQualifyingSourcesZero && numberOfReportedSources > 0

  return (
    <>
      <div>
        <Card>
          <ReportSectionCardHeader
            icon={<LightningIcon className="size-4" />}
            title={t('report:electricity.title')}
            completionPercentage={completionPercentage}
          />
          <CardBody className="space-y-8">
            {showZeroConsumptionWarning && (
              <Alert type="error">{t('report:electricity.zeroConsumptionAllSourcesWarning')}</Alert>
            )}
            <section className="space-y-2">
              <div className="flex items-center gap-1">
                <h4 className="font-medium text-sm text-gray-600">
                  {t('report:electricity.sourceConfiguration.title')}
                </h4>
                <InfoTooltip content={t('report:electricity.sourceConfiguration.callout')} />
              </div>

              {hasAtLeastOneMethodConfig && (
                <SourceConfigurationTable
                  electricityProductionMethods={electricityProductionMethods}
                  config={config}
                  isViewMode={isViewMode}
                  startEditMethodDialog={startEditMethodDialog}
                />
              )}

              <div className="flex gap-2">
                {electricityProductionMethods
                  .filter(
                    method =>
                      config[method.type]?.consumption === undefined ||
                      config[method.type]?.consumption === null
                  )
                  .map(method => {
                    return (
                      <Button
                        key={method.type}
                        icon={<PlusIcon />}
                        variant="subtle"
                        sizeType="sm"
                        onClick={() => startEditMethodDialog(method.type)}
                      >
                        {t(`common:electricitySources.${method.type}.title`)}
                      </Button>
                    )
                  })}
              </div>
            </section>

            <section className="space-y-2">
              <h4 className="font-medium text-sm text-gray-600 flex items-center gap-1">
                {t('report:electricity.methodConsumption.title')}
                <InfoTooltip content={t('report:electricity.methodConsumption.callout')} />
              </h4>

              {hasAtLeastOneMethodEmissionReport && (
                <Table dense>
                  <thead>
                    <TableRow>
                      <TableColumnHeader className="w-52">
                        {t('report:electricity.methodConsumption.table.method')}
                      </TableColumnHeader>
                      <TableColumnHeader className="w-32">
                        {t('report:electricity.methodConsumption.table.consumption')} (
                        {UnitLabel.MWh})
                      </TableColumnHeader>
                      {electricityProductionMethods.map(method => {
                        return (
                          <TableColumnHeader key={method.type} className="w-32 text-right">
                            {t(`common:electricitySources.${method.type}.title`)} ({UnitLabel.tCO2e}
                            )
                          </TableColumnHeader>
                        )
                      })}
                      <TableColumnHeader className="w-32"></TableColumnHeader>
                    </TableRow>
                  </thead>
                  <tbody>
                    {methodEmissionReports.map(methodEmissionReport => {
                      const { consumptionTj } = methodEmissionReport.electricity || {}
                      const hasValue = consumptionTj !== undefined && consumptionTj !== null
                      return (
                        <TableRow key={methodEmissionReport.id}>
                          <TableRowHeader className="text-left">
                            {t(
                              `common:productionMethods.${methodEmissionReport.productionMethodCode}`
                            )}
                          </TableRowHeader>
                          <TableCell className="text-right text-sm">
                            {hasValue ? (
                              <LocalizedNumberFlow
                                value={convertTjToMwh(consumptionTj as number)}
                              />
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          {electricityProductionMethods.map(method => {
                            const calculatedEmissionsSummary = calculatedEmissionsForAllMethodsById
                              ? calculatedEmissionsForAllMethodsById[methodEmissionReport.id]
                              : null
                            const sourceEmission =
                              calculatedEmissionsSummary?.electricityEmissionsBreakdown[method.type]
                                ?.emission || 0
                            return (
                              <TableCell key={method.type} className="text-right text-sm">
                                {sourceEmission > 0 ? <NumberFlow value={sourceEmission} /> : '-'}
                              </TableCell>
                            )
                          })}
                          <TableCell className="text-right">
                            <Button
                              disabled={isViewMode}
                              sizeType="sm"
                              variant="txt-default"
                              onClick={() => initProductionMethodEdit(methodEmissionReport.id)}
                            >
                              {hasValue ? t('common:actions.edit') : t('common:actions.add')}
                            </Button>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </tbody>
                </Table>
              )}

              <div className="flex gap-2">
                {methodEmissionReports
                  .filter(methodEmissionReport => !methodEmissionReport.electricity?.consumptionTj)
                  .map(method => {
                    return (
                      <Button
                        key={method.id}
                        icon={<PlusIcon />}
                        variant="subtle"
                        sizeType="sm"
                        onClick={() => initProductionMethodEdit(method.id)}
                      >
                        {t(`common:productionMethods.${method.productionMethodCode}`)}
                      </Button>
                    )
                  })}
              </div>
            </section>
          </CardBody>
        </Card>
      </div>
      <ElectricitySourceDialog
        isOpen={isEditMethodDialogOpen}
        close={closeEditMethodDialog}
        onAfterLeave={resetCurrentEditedMethod}
        method={currentEditedElectricityMethodType as ElectricityProductionMethod}
        save={saveElectricityConfig}
        isSaving={isUpdatingEmissionReport}
        defaultGridEmissionFactor={
          currentEditedElectricityMethodType === ElectricityProductionMethod.GRID
            ? gridEmissionFactor
            : null
        }
        isRenewable={isCurrentEditedMethodRenewable}
        initialValues={
          currentEditedElectricityMethodType ? config[currentEditedElectricityMethodType] : null
        }
      />
      <ProductionMethodConsumptionEditDialog
        reportId={reportId}
        methodReportId={currentMethodReportId!}
        initialValue={editedMethodEmissionReport?.electricity?.consumptionTj || 0}
        consumptionUnit={editedMethodEmissionReport?.electricity?.unit || null}
        isOpen={isEditProductionMethodConsumptionDialogOpen}
        close={closeProductionMethodEdit}
        productionMethodName={editedProductionMethodName}
        reset={resetProductionMethodEdit}
      />
    </>
  )
}
